import { createRouter, createWebHistory, RouteLocationNormalizedGeneric } from 'vue-router';
import ApiBase from '@/api/base';
import { CurretUser, CurrentAdminUser } from '@/api/openapi';
import { useCurrentUserProvider } from '@/composable/current-user';
import AuthService from '@/services/auth_service';
import store from '../store';
import { adminRoutes } from './admin';
import { authRoutes } from './auth';
import { userRoutes } from './user';
import { vaAppRoutes } from './va-app';

type RuleType = 'isNoSignIn' | 'isAdmin' | 'isAdminDeveloper' | 'isAdminOwner' | 'isCrmUser' | 'isCrmOwnerMember' | 'isVaUser' | 'all';

declare module 'vue-router' {
  interface RouteMeta {
    rule?: RuleType;
    title?: string;
    requiresAuth?: boolean;
  }
}

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { left: 0, top: 0 };
    }
  },
  routes: [
    ...authRoutes,
    ...userRoutes,
    ...vaAppRoutes,
    ...adminRoutes,
    {
      path: '/*',
      redirect: '/error',
      meta: {
        rule: 'all',
      },
    },
  ],
});

const authService = new AuthService();

const clearCondition = async () => {
  await store.dispatch('userUi/clearLeadListFilterParamsAction');
  await store.dispatch('userUi/clearLeadListSortOrderAction');
  await store.dispatch('userUi/clearLeadListSortKeyAction');
  await store.dispatch('user/clearLeadViewAction');
};

type Rule = {
  roles: string[];
  appType?: string;
};
const globalRules: Record<RuleType, Rule>= {
  isNoSignIn: {
    roles: ['nosignin'],
  },
  isAdmin: {
    roles: ['admin_owner', 'admin_member', 'admin_developer'],
  },
  isAdminDeveloper: {
    roles: ['admin_owner', 'admin_developer'],
  },
  isAdminOwner: {
    roles: ['admin_owner'],
  },
  isCrmUser: {
    roles: ['owner', 'member', 'bpo_guest'],
    appType: 'crm',
  },
  isCrmOwnerMember: {
    roles: ['owner', 'member'],
    appType: 'crm',
  },
  isVaUser: {
    roles: ['owner', 'member'],
    appType: 'voice_analytics',
  },
  all: {
    roles: ['nosignin', 'admin_owner', 'admin_member', 'admin_developer', 'owner', 'member', 'bpo_guest'],
  },
};

const canRoute = async (rule: RuleType, currentUser: CurretUser | CurrentAdminUser | null) => {
  // TODO: firebaseに未ログインならnosigninにする
  const role = globalRules.all.roles.find((r) => r === currentUser?.role) || 'nosignin';
  return globalRules[rule].roles.includes(role) && (globalRules[rule].appType === undefined || globalRules[rule].appType === currentUser?.appType);
};

const fetchCurrentUser = async (to: RouteLocationNormalizedGeneric, refetchCurrentUser: () => Promise<void>, refetchCurrentAdminUser: () => Promise<void>) => {
  if (['isNoSignIn', 'all'].includes(to.meta.rule)) {
    return true;
  }
  if (to.path.startsWith('/admin')) {
    await refetchCurrentAdminUser();
  } else {
    await refetchCurrentUser();
  }
  return true;
};

const isVaAppPath = (path: string) => /^\/va-app(\/|$)/.test(path);
const isAdminPath = (path: string) => /^\/admin(\/|$)/.test(path);

const setPageTitle = (title: string | undefined, path: string) => {
  if (isAdminPath(path)) {
    document.title = title ?? 'BALES CLOUD';
    return;
  }
  const appName = isVaAppPath(path) ? 'Voice Analytics' : 'BALES CLOUD';
  document.title = title ? `${title} - ${appName}` : appName;
};

const setFavicon = (path: string) => {
  const link = document.getElementById('favicon');
  if (!(link instanceof HTMLLinkElement)) {
    return;
  }
  link.href = isVaAppPath(path) ? '/va-app-favicon.svg' : '/favicon.ico';
};

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  ApiBase.cancel('Request canceled by route change.');

  // NOTE: inject結果はここでないと取得できなかった。（おそらくこのタスク内でないと（キューイングされて別タスクになると）取得できない）
  const { inject: injectCurrentUser } = useCurrentUserProvider();
  const { currentUser, currentAdminUser, refetchCurrentUser, refetchCurrentAdminUser } = injectCurrentUser();

  // firebaseの認証を待つ
  await authService.authAsync();
  const firebaseCurrentUser = authService.currentUser();

  // リードリストから遷移する際はフィルタをクリアする
  if (
    from.name === 'LeadList'
    && (
      to.name !== 'LeadList'
      && to.name !== 'Transcription'
    )
  ) {
    await clearCondition();
  }

  // アナリティクスから遷移する際はフィルタをクリアする
  if (from.name === 'CallAnalytics') {
    await clearCondition();
  }

  if (requiresAuth && !firebaseCurrentUser) {
    const basePath = /^\/(app|admin|va-app)\/*/.exec(to.path);
    const appTypePathPart = Array.isArray(basePath) && basePath.length >= 2 ? basePath[1] : 'app';
    next({
      path: `/${appTypePathPart}/login`,
      query: {
        dest_location: to.fullPath,
      },
    });
  } else if (
    to.meta.rule
    && await fetchCurrentUser(to, refetchCurrentUser, refetchCurrentAdminUser)
    && await canRoute(to.meta.rule, currentUser.value || currentAdminUser.value)
  ) {
    next();
  } else {
    next({
      path: '/error',
    });
  }
  setPageTitle(to.meta?.title, to.path);
  setFavicon(to.path);
});

export default router;
